import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  XMarkIcon,
  PlayIcon,
  StopIcon,
  ClockIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

function formatTs(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function hostLabel(host) {
  return host === 'api.x.com' ? 'api.x.com' : 'x.com';
}

function formatDuration(ms) {
  if (!ms) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ── Auto-refresh panel ────────────────────────────────────────────────────────

function AutoRefreshPanel({ agents }) {
  const [config, setConfig]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Local editable fields
  const [enabled, setEnabled]           = useState(false);
  const [intervalHours, setIntervalHours] = useState(24);
  const [targetAgentId, setTargetAgentId] = useState('');
  const [targetUser, setTargetUser]     = useState('elonmusk');
  const [targetTweetId, setTargetTweetId] = useState('');
  const [searchQuery, setSearchQuery]   = useState('onlyfans.com');
  const [browserEngine, setBrowserEngine] = useState('orbex');

  // Full config load — resets form fields. Call on mount and after Save.
  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/graphql-endpoints/auto-refresh/status');
      if (res.data.success) {
        const c = res.data.config;
        setConfig(c);
        setEnabled(c.enabled || false);
        setIntervalHours(c.intervalHours || 24);
        setTargetAgentId(c.targetAgentId || '');
        setTargetUser(c.targetUser || 'elonmusk');
        setTargetTweetId(c.targetTweetId || '');
        setSearchQuery(c.searchQuery || 'onlyfans.com');
        setBrowserEngine(c.browserEngine === 'gologin' ? 'gologin' : 'orbex');
      }
    } catch (err) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, []);

  // Status-only refresh — updates run state for display without touching form fields.
  // Used by the 10s poll and after Run Now / Stop so unsaved form edits are preserved.
  const refreshStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/graphql-endpoints/auto-refresh/status');
      if (res.data.success) setConfig(res.data.config);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchConfig();
    // Poll for running status every 10s without clobbering form fields.
    const t = setInterval(refreshStatus, 10_000);
    return () => clearInterval(t);
  }, [fetchConfig, refreshStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/graphql-endpoints/auto-refresh/config', {
        enabled,
        intervalHours,
        targetAgentId: targetAgentId || null,
        targetUser,
        targetTweetId,
        searchQuery,
        browserEngine,
      });
      if (res.data.success) {
        setConfig(res.data.config);
        toast.success('Auto-refresh config saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setTriggering(true);
    try {
      // Run now used to ignore the username still sitting in the box.
      const saved = await api.put('/admin/graphql-endpoints/auto-refresh/config', {
        enabled,
        intervalHours,
        targetAgentId: targetAgentId || null,
        targetUser,
        targetTweetId,
        searchQuery,
        browserEngine,
      });
      if (!saved.data?.success) {
        toast.error(saved.data?.error || 'Failed to save config');
        return;
      }
      setConfig(saved.data.config);
      const res = await api.post('/admin/graphql-endpoints/auto-refresh/trigger');
      if (res.data.success) {
        toast.success('Refresh job dispatched — desktop-agent will pick it up shortly');
        await refreshStatus();
      } else {
        toast.error(res.data.error || 'Failed to trigger');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to trigger');
    } finally {
      setTriggering(false);
    }
  };

  const handleSelfTest = async () => {
    setTriggering(true);
    try {
      const saved = await api.put('/admin/graphql-endpoints/auto-refresh/config', {
        enabled,
        intervalHours,
        targetAgentId: targetAgentId || null,
        targetUser,
        targetTweetId,
        searchQuery,
        browserEngine,
        selfTest: true,
      });
      if (!saved.data?.success) {
        toast.error(saved.data?.error || 'Failed to save config');
        return;
      }
      setConfig(saved.data.config);
      const res = await api.post('/admin/graphql-endpoints/auto-refresh/trigger');
      if (res.data.success) {
        toast.success('Self-test dispatched — two idle accounts on that VPS');
        await refreshStatus();
      } else {
        toast.error(res.data.error || 'Failed to start self-test');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start self-test');
    } finally {
      setTriggering(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      await api.post('/admin/graphql-endpoints/auto-refresh/stop');
      toast.success('Automation stopped');
      await refreshStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  if (loading) return null;

  const isRunning = config?.isRunning;
  const lastResult = config?.lastRunResult;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <CpuChipIcon className="w-5 h-5 text-indigo-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">GraphQL Auto-refresh</h3>
        {isRunning && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            Running
          </span>
        )}
      </div>

      {/* Config grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* VA Agent */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            VA Agent VPS
          </label>
          <select
            value={targetAgentId}
            onChange={(e) => setTargetAgentId(e.target.value)}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          >
            <option value="">— Select agent —</option>
            {(agents || []).map((a) => (
              <option key={a._id} value={a._id}>{a.name}</option>
            ))}
          </select>
        </div>

        {/* Browser */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Browser
          </label>
          <select
            value={browserEngine}
            onChange={(e) => setBrowserEngine(e.target.value)}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          >
            <option value="orbex">Orbex</option>
            <option value="gologin">GoLogin</option>
          </select>
        </div>

        {/* Interval */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Run every
          </label>
          <select
            value={intervalHours}
            onChange={(e) => setIntervalHours(Number(e.target.value))}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          >
            <option value={2}>2 hours</option>
            <option value={6}>6 hours</option>
            <option value={12}>12 hours</option>
            <option value={24}>24 hours</option>
          </select>
        </div>

        {/* Target user */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Target X username
          </label>
          <input
            type="text"
            value={targetUser}
            onChange={(e) => setTargetUser(e.target.value.replace(/^@/, ''))}
            placeholder="elonmusk"
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
        </div>

        {/* Target tweet */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Tweet ID <span className="font-normal text-gray-400">(for TweetDetail — optional)</span>
          </label>
          <input
            type="text"
            value={targetTweetId}
            onChange={(e) => setTargetTweetId(e.target.value.trim())}
            placeholder="1234567890"
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
        </div>

        {/* Search keyword */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Search keyword <span className="font-normal text-gray-400">(for SearchTimeline)</span>
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="onlyfans.com"
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
        </div>

        {/* Enable toggle */}
        <div className="flex items-end">
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {enabled ? 'Automation enabled' : 'Automation disabled'}
            </span>
          </label>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
        >
          {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : null}
          Save
        </button>

        <button
          type="button"
          onClick={handleRunNow}
          disabled={triggering || isRunning}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
        >
          {triggering
            ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
            : <PlayIcon className="w-4 h-4" />}
          Run now
        </button>

        <button
          type="button"
          onClick={handleSelfTest}
          disabled={triggering || isRunning || !targetAgentId}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
        >
          {triggering
            ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
            : <PlayIcon className="w-4 h-4" />}
          Run self-test
        </button>

        {isRunning && (
          <button
            type="button"
            onClick={handleStop}
            disabled={stopping}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {stopping
              ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
              : <StopIcon className="w-4 h-4" />}
            Stop
          </button>
        )}
      </div>

      {/* Status line */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
        {config?.lastRunAt && (
          <span className="flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5" />
            Last run: {formatTs(config.lastRunAt)}
            {lastResult?.durationMs ? ` (${formatDuration(lastResult.durationMs)})` : ''}
            {lastResult?.accountUsername ? ` · @${lastResult.accountUsername}` : ''}
          </span>
        )}
        {lastResult && !lastResult.error && (
          <span>
            {lastResult.updated} updated · {lastResult.unchanged} unchanged · {lastResult.notCaptured} not captured
            {lastResult.updatedKeys?.length > 0 && (
              <span className="text-indigo-500 ml-1">({lastResult.updatedKeys.join(', ')})</span>
            )}
          </span>
        )}
        {lastResult?.error && (
          <span className="text-red-500">Error: {lastResult.error}</span>
        )}
        {config?.selfTestResult?.outcome && (
          <span>
            Self-test: {config.selfTestResult.outcome}
            {config.selfTestResult.fingerprintChanged ? ' · fingerprint changed' : ''}
            {config.selfTestResult.imageSent === false ? ' · image failed' : ''}
          </span>
        )}
        {config?.nextRunAt && config?.enabled && !isRunning && (
          <span className="flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5" />
            Next: {formatTs(config.nextRunAt)}
          </span>
        )}
      </div>
    </div>
  );
}

function prettyBody(text) {
  if (!text) return '';
  const trimmed = String(text).trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return text;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return text;
  }
}

function HeaderBlock({ title, headers }) {
  const entries = Object.entries(headers || {});
  if (!entries.length) {
    return <p className="text-xs text-gray-500 dark:text-gray-400">{title}: none</p>;
  }
  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{title}</div>
      <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-900/60 rounded-lg p-3 max-h-56 overflow-auto">
        {entries.map(([k, v]) => `${k}: ${v}`).join('\n')}
      </pre>
    </div>
  );
}

function ManualCapturePanel({ agents }) {
  const [status, setStatus] = useState(null);
  const [agentId, setAgentId] = useState('');
  const [browserEngine, setBrowserEngine] = useState('gologin'); // 'gologin' | 'orbex'
  const [username, setUsername] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [events, setEvents] = useState([]);
  const [viewSessionId, setViewSessionId] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedSeq, setSelectedSeq] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const lastSeqRef = useRef(0);

  const active = status?.active;
  const sessionId = viewSessionId || (active && String(active._id)) || '';
  const selectedAgent = (agents || []).find(a => String(a._id) === agentId);
  const orbexAvailable = Boolean(selectedAgent?.runtime?.orbexInstalled);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/graphql-endpoints/manual-capture/status');
      if (res.data.success) setStatus({ active: res.data.active, recent: res.data.recent || [] });
    } catch (_) {}
  }, []);

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 4_000);
    return () => clearInterval(t);
  }, [refreshStatus]);

  useEffect(() => {
    if (!agentId || username.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/admin/graphql-endpoints/manual-capture/accounts', {
          params: { agentId, q: username.trim() },
        });
        if (res.data.success) setSuggestions(res.data.accounts || []);
      } catch (_) {}
    }, 250);
    return () => clearTimeout(t);
  }, [agentId, username]);

  const loadEvents = useCallback(async (sid, reset) => {
    if (!sid) return;
    if (reset) {
      lastSeqRef.current = 0;
      setEvents([]);
    }
    try {
      const res = await api.get('/admin/graphql-endpoints/manual-capture/events', {
        params: { sessionId: sid, afterSeq: lastSeqRef.current, limit: 200 },
      });
      const batch = res.data.events || [];
      if (!batch.length) return;
      lastSeqRef.current = batch[batch.length - 1].seq;
      setEvents((prev) => {
        const seen = new Set(prev.map((e) => e.seq));
        const next = [...prev];
        for (const ev of batch) {
          if (!seen.has(ev.seq)) next.push(ev);
        }
        return next;
      });
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setEvents([]);
      lastSeqRef.current = 0;
      return undefined;
    }
    loadEvents(sessionId, true);
    const t = setInterval(() => loadEvents(sessionId, false), 2_000);
    return () => clearInterval(t);
  }, [sessionId, loadEvents]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await api.post('/admin/graphql-endpoints/manual-capture/start', {
        agentId,
        username,
        browserEngine,
      });
      if (res.data.success) {
        const engineLabel = browserEngine === 'orbex' ? 'Orbex' : 'GoLogin';
        toast.success(`Opening @${res.data.session.username} on ${res.data.session.agentName} — desktop-agent will launch ${engineLabel}`);
        setViewSessionId(String(res.data.session._id));
        await refreshStatus();
      } else {
        toast.error(res.data.error || 'Failed to start');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      await api.post('/admin/graphql-endpoints/manual-capture/stop', {
        sessionId: active?._id,
      });
      toast.success('Stop sent — browser will close on the VPS');
      await refreshStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  const openEvent = async (seq) => {
    if (!sessionId || seq == null) return;
    setSelectedSeq(String(seq));
    setDetailLoading(true);
    try {
      const res = await api.get(
        `/admin/graphql-endpoints/manual-capture/sessions/${sessionId}/seq/${seq}`
      );
      if (res.data.success) setDetail(res.data.event);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load request');
    } finally {
      setDetailLoading(false);
    }
  };

  const isLive = active && (active.status === 'pending' || active.status === 'running');
  const filtered = events.filter((ev) => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return (
      (ev.summary || '').toLowerCase().includes(q)
      || (ev.graphqlOp || '').toLowerCase().includes(q)
      || (ev.url || '').toLowerCase().includes(q)
      || (ev.method || '').toLowerCase().includes(q)
      || String(ev.status || '').includes(q)
    );
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6 space-y-5">
      <div className="flex items-center gap-3">
        <MagnifyingGlassIcon className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Manual capture VPS</h3>
        {isLive && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {active.status === 'pending' ? 'Waiting for VPS' : 'Capturing'}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Launch a GoLogin profile on the selected VPS and capture every X / XChat request + response
        (headers and bodies). Log in with the pin, open threads, send photos — then compare against
        the chatbot API traffic. Requires desktop-agent v0.3.83+ on that VPS.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">VPS</label>
          <select
            value={agentId}
            onChange={(e) => { setAgentId(e.target.value); setSuggestions([]); setBrowserEngine('gologin'); }}
            disabled={isLive}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          >
            <option value="">— Select VPS —</option>
            {(agents || []).map((a) => (
              <option key={a._id} value={a._id}>{a.name}</option>
            ))}
          </select>

          {/* Browser engine selector — only shown once a VPS is selected */}
          {agentId && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Browser:</span>
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs font-medium">
                <button
                  type="button"
                  disabled={isLive}
                  onClick={() => setBrowserEngine('gologin')}
                  className={`px-3 py-1.5 transition-colors ${
                    browserEngine === 'gologin'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  GoLogin / Orbita
                </button>
                <button
                  type="button"
                  disabled={isLive || !orbexAvailable}
                  onClick={() => orbexAvailable && setBrowserEngine('orbex')}
                  title={orbexAvailable ? 'Launch with Orbex Browser' : 'Orbex not installed on this VPS'}
                  className={`px-3 py-1.5 transition-colors border-l border-gray-200 dark:border-gray-700 ${
                    !orbexAvailable
                      ? 'opacity-40 cursor-not-allowed bg-white dark:bg-gray-900 text-gray-400'
                      : browserEngine === 'orbex'
                        ? 'bg-orange-500 text-white'
                        : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-600'
                  }`}
                >
                  Orbex {!orbexAvailable && '(not installed)'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Account username
          </label>
          <input
            type="text"
            value={username}
            disabled={isLive}
            onChange={(e) => setUsername(e.target.value.replace(/^@/, ''))}
            placeholder="amybliss3247dl"
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
          {suggestions.length > 0 && !isLive && (
            <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
              {suggestions.map((s) => (
                <button
                  key={s._id}
                  type="button"
                  onClick={() => { setUsername(s.username); setSuggestions([]); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  @{s.username}
                  <span className="ml-2 text-xs text-gray-400">{s.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={handleStart}
            disabled={starting || isLive || !agentId || !username.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          >
            {starting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
            Open & capture
          </button>
          {isLive && (
            <button
              type="button"
              onClick={handleStop}
              disabled={stopping}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {stopping ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <StopIcon className="w-4 h-4" />}
              Stop
            </button>
          )}
        </div>
      </div>

      {active && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          @{active.username} on {active.agentName} · {active.eventCount || 0} events
          {active.status === 'pending' ? ' · waiting for desktop-agent pull' : ''}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
            Requests ({filtered.length}{filter ? ` / ${events.length}` : ''})
          </label>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter GraphQL op, URL, status…"
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
          {(status?.recent || []).length > 0 && (
            <select
              value={sessionId}
              onChange={(e) => {
                setViewSessionId(e.target.value);
                setDetail(null);
                setSelectedSeq('');
              }}
              className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              <option value="">Live / pick a past session</option>
              {(status.recent || []).map((s) => (
                <option key={s._id} value={s._id}>
                  {formatTs(s.createdAt)} · @{s.username} · {s.status} · {s.eventCount || 0}
                </option>
              ))}
            </select>
          )}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-[420px] overflow-auto divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-xs text-gray-500 text-center">
                No requests yet. Start a capture and use XChat on the VPS.
              </div>
            )}
            {filtered.map((ev) => (
              <button
                key={ev.seq}
                type="button"
                onClick={() => openEvent(ev.seq)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900/60 ${String(ev.seq) === selectedSeq ? 'bg-indigo-50 dark:bg-indigo-950/40' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-mono text-gray-500">{ev.method}</span>
                  <span className={`text-[11px] font-mono ${ev.status >= 400 ? 'text-red-500' : 'text-gray-400'}`}>
                    {ev.status ?? (ev.kind === 'ws' ? 'ws' : '…')}
                  </span>
                </div>
                <div className="text-xs text-gray-900 dark:text-white truncate">{ev.summary || ev.graphqlOp || ev.url}</div>
                {ev.graphqlOp && (
                  <div className="text-[10px] text-indigo-500 truncate">{ev.graphqlOp}</div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 min-h-[280px] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          {detailLoading && (
            <div className="text-sm text-gray-500">Loading request…</div>
          )}
          {!detailLoading && !detail && (
            <div className="text-sm text-gray-500">Select a request to inspect headers and raw bodies.</div>
          )}
          {!detailLoading && detail && (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white break-all">
                  {detail.method} {detail.status != null ? detail.status : ''} {detail.summary}
                </div>
                <div className="text-[11px] font-mono text-gray-500 dark:text-gray-400 break-all mt-1">{detail.url}</div>
                {detail.graphqlHash && (
                  <div className="text-[11px] font-mono text-indigo-500 mt-1">
                    {detail.graphqlOp} · {detail.graphqlHash}
                    {detail.durationMs != null ? ` · ${detail.durationMs}ms` : ''}
                  </div>
                )}
              </div>
              <HeaderBlock title="Request headers" headers={detail.requestHeaders} />
              <div>
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Request body{detail.requestBodyTruncated ? ' (truncated)' : ''}
                </div>
                <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-900/60 rounded-lg p-3 max-h-64 overflow-auto">
                  {prettyBody(detail.requestBody) || '—'}
                </pre>
              </div>
              <HeaderBlock title="Response headers" headers={detail.responseHeaders} />
              <div>
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Response body{detail.responseBodyTruncated ? ' (truncated)' : ''}
                  {detail.responseBodyBytes ? ` · ${detail.responseBodyBytes} bytes` : ''}
                </div>
                <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-900/60 rounded-lg p-3 max-h-80 overflow-auto">
                  {prettyBody(detail.responseBody) || '—'}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

const GhashTab = () => {
  const [loading, setLoading]       = useState(true);
  const [groups, setGroups]         = useState({});
  const [total, setTotal]           = useState(0);
  const [editingKey, setEditingKey] = useState(null);
  const [editHash, setEditHash]     = useState('');
  const [saving, setSaving]         = useState(false);
  const [agents, setAgents]         = useState([]);

  const fetchEndpoints = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/graphql-endpoints');
      if (res.data.success) {
        setGroups(res.data.groups || {});
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load GraphQL endpoints');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch VA agents for the agent selector
  useEffect(() => {
    api.get('/admin/va/ai-agents')
      .then((res) => {
        if (res.data.success) setAgents(res.data.agents || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchEndpoints();
  }, [fetchEndpoints]);

  const categoryOrder = useMemo(() => {
    const preferred = [
      'Platform (shared)',
      'Follow-backs',
      'VA Automation',
      'Chatbot automation',
      'Account updates',
      'Scraper & sourcing',
      'XChat & keys',
      'Mass tasks',
    ];
    const keys = Object.keys(groups);
    return [
      ...preferred.filter((c) => keys.includes(c)),
      ...keys.filter((c) => !preferred.includes(c)).sort(),
    ];
  }, [groups]);

  const startEdit = (item) => {
    if (!item.editable) return;
    setEditingKey(item.key);
    setEditHash(item.effectiveHash || item.hash || item.defaultHash || '');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditHash('');
  };

  const saveEdit = async (key) => {
    const trimmed = editHash.trim();
    if (!trimmed) {
      toast.error('Hash cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/admin/graphql-endpoints/${encodeURIComponent(key)}`, { hash: trimmed });
      if (res.data.success) {
        toast.success('GraphQL hash updated — applies on next request');
        setEditingKey(null);
        setEditHash('');
        await fetchEndpoints();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update hash');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading GraphQL hashes…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header panel */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ghash — GraphQL endpoints</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {total} operation{total === 1 ? '' : 's'} used across the platform. Editable hashes apply immediately at runtime.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchEndpoints}
            className="inline-flex items-center gap-2 btn-secondary"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Followers auto-refreshes every 5 hours; HTTP 404 on an endpoint flags it as suspected stale below.
        </p>
      </div>

      {/* GraphQL auto-refresh control panel */}
      <AutoRefreshPanel agents={agents} />

      {/* Manual capture VPS */}
      <ManualCapturePanel agents={agents} />

      {/* Endpoint category tables */}
      {categoryOrder.map((category) => (
        <div key={category} className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{category}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {(groups[category] || []).length} endpoint{(groups[category] || []).length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-gray-500 bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="text-left py-3 px-4">Operation</th>
                  <th className="text-left py-3 px-4">Path</th>
                  <th className="text-left py-3 px-4">Hash</th>
                  <th className="text-left py-3 px-4">Last updated</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(groups[category] || []).map((item) => {
                  const isEditing = editingKey === item.key;
                  const source = item.lastUpdatedSource || 'manual';
                  return (
                    <tr key={item.key} className={item.suspectedStale ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}>
                      <td className="py-3 px-4 align-top">
                        <div className="font-medium text-gray-900 dark:text-white">{item.operationName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">{item.description}</div>
                        {item.usedBy?.length > 0 && (
                          <div className="text-[11px] text-indigo-600 dark:text-indigo-400 mt-1">
                            {item.usedBy.join(' · ')}
                          </div>
                        )}
                        {item.autoRefreshEnabled && (
                          <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                            5h auto-refresh
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 align-top font-mono text-xs text-gray-600 dark:text-gray-300">
                        <div>{hostLabel(item.host)}</div>
                        <div className="mt-1 break-all">{item.pathTemplate}</div>
                      </td>
                      <td className="py-3 px-4 align-top font-mono text-xs">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editHash}
                            onChange={(e) => setEditHash(e.target.value)}
                            className="w-full min-w-[200px] px-2 py-1.5 border border-indigo-300 dark:border-indigo-600 rounded bg-white dark:bg-gray-900"
                            placeholder="Operation hash"
                          />
                        ) : (
                          <span className="text-gray-900 dark:text-white break-all">{item.effectiveHash}</span>
                        )}
                        {!isEditing && item.hash && item.hash !== item.defaultHash && (
                          <div className="text-[10px] text-gray-400 mt-1">default: {item.defaultHash}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 align-top text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        <div>{formatTs(item.lastUpdatedAt)}</div>
                        {source === 'ai_va' ? (
                          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                            <CpuChipIcon className="w-3 h-3" />
                            AI VA
                          </span>
                        ) : item.lastUpdatedAt ? (
                          <span className="text-[10px] text-gray-400">manual</span>
                        ) : null}
                      </td>
                      <td className="py-3 px-4 align-top">
                        {item.suspectedStale ? (
                          <span className="inline-flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{item.suspectedStaleReason || 'Suspected stale hash'}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                            <CheckCircleIcon className="w-4 h-4" />
                            OK
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 align-top">
                        {item.editable && (
                          isEditing ? (
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => saveEdit(item.key)}
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-0.5"
                              >
                                <XMarkIcon className="w-3.5 h-3.5" />
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startEdit(item)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                            >
                              <PencilSquareIcon className="w-3.5 h-3.5" />
                              Update
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GhashTab;
